import React, { useEffect, useMemo, useState } from "react";
import {
  DataGrid,
  gridClasses,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  OutlinedInput,
  IconButton,
  InputAdornment,
  CircularProgress,
  Box,
  Popover,
  Alert,
  Typography,
  Link as MuiLink,
  Breadcrumbs,
  Divider,
  ListItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import InfoIcon from "@mui/icons-material/Info";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { ROUTES } from "../../constants";
import { userSchema } from "./schemas";
import { useUsersManagement } from "./useUsersManagement";
import { DEFAULT_FORM_VALUES } from "./constants";
import { FixedSizeList } from "react-window";

const TOGGLE_ALL_OPTION = { id: "TOGGLE ALL", label: "Toggle All" };

const ITEM_HEIGHT = 40;

const VirtualizedList = ({ items }) => (
  <FixedSizeList
    height={Math.min(items.length * ITEM_HEIGHT, 300)}
    width={500}
    itemSize={ITEM_HEIGHT}
    itemCount={items.length}
    itemData={items}
  >
    {({ index, style, data }) => (
      <Box key={index} style={style}>
        <ListItem
          sx={{
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {data[index]}
          </Typography>
        </ListItem>
        {index < data.length - 1 && <Divider />}
      </Box>
    )}
  </FixedSizeList>
);

const ColumnsPopover = ({ name, items }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Button size="small" variant="outlined" onClick={handleClick}>
        View {name} ({items.length})
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              border: "1px solid gray",
            },
          },
        }}
      >
        <VirtualizedList items={items} />
      </Popover>
    </>
  );
};

export default function UsersManagement() {
  // Local state
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [infoAnchor, setInfoAnchor] = useState(null);
  const [searchText, setSearchText] = useState("");

  const {
    usersQuery,
    permitsQuery,
    rolesQuery,
    createOrUpdateMutation,
    deleteMutation,
    resetPasswordMutation,
  } = useUsersManagement();

  // RHF
  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  // Data
  const {
    data: users,
    error: errorUsers,
    isLoading: loadingUsers,
  } = usersQuery;
  const { data: permits, isLoading: loadingPermits } = permitsQuery;
  const { data: roles, isLoading: loadingRoles } = rolesQuery;

  const normalizedNeedle = searchText.trim().toLowerCase();

  const rolesById = useMemo(() => {
    const map = new Map();
    for (const role of roles || []) {
      if (role?.id) map.set(role.id, role);
    }
    return map;
  }, [roles]);

  const permitsById = useMemo(() => {
    const map = new Map();
    for (const permit of permits || []) {
      if (permit?.permit_ndx != null) map.set(permit.permit_ndx, permit);
    }
    return map;
  }, [permits]);

  const rowMatchesSearch = useMemo(() => {
    if (!normalizedNeedle) return () => true;

    return (row) => {
      const permitIds = Array.isArray(row?.permits) ? row.permits : [];
      const roleIds = Array.isArray(row?.roles) ? row.roles : [];

      const roleNames = roleIds
        .map((id) => rolesById.get(id)?.name)
        .filter(Boolean);

      const permitLabels = permitIds
        .map((id) => {
          const p = permitsById.get(id);
          return p
            ? [`#${p.permit_ndx}`, p.contact_name, p.permittee]
                .filter(Boolean)
                .join(" | ")
            : `#${id}`;
        })
        .filter(Boolean);

      const searchable = [
        row?.auth0_user_id,
        row?.email,
        row?.name,
        row?.nickname,
        ...roleIds,
        ...roleNames,
        ...permitIds,
        ...permitLabels,
      ]
        .filter((v) => v !== undefined && v !== null)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedNeedle);
    };
  }, [normalizedNeedle, permitsById, rolesById]);

  const filteredUsers = useMemo(() => {
    const allUsers = users || [];
    if (!normalizedNeedle) return allUsers;
    return allUsers.filter(rowMatchesSearch);
  }, [users, normalizedNeedle, rowMatchesSearch]);

  useEffect(() => {
    if (!selectedUserId) return;
    const stillVisible = filteredUsers.some(
      (u) => u.auth0_user_id === selectedUserId
    );
    if (!stillVisible) {
      setSelectedUserId(null);
    }
  }, [filteredUsers, selectedUserId]);

  const sortedPermits = useMemo(() => {
    return (permits || [])
      .slice()
      .sort((a, b) => Number(a?.permit_ndx ?? 0) - Number(b?.permit_ndx ?? 0));
  }, [permits]);

  /* -------------------------
     Handlers
  ------------------------- */
  const handleAddUser = () => {
    setIsCreateMode(true);
    reset(DEFAULT_FORM_VALUES);
    setOpenDialog(true);
  };

  const handleEditUser = () => {
    if (!selectedUserId || !users) return;
    const userToEdit = users.find((u) => u.auth0_user_id === selectedUserId);
    if (!userToEdit) return;

    setIsCreateMode(false);
    reset({
      auth0_user_id: userToEdit.auth0_user_id,
      email: userToEdit.email || "",
      name: userToEdit.name || "",
      nickname: userToEdit.nickname || "",
      password: "",
      roles: userToEdit.roles || [],
      permits: userToEdit.permits || [],
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = (_event, reason) => {
    if (reason === "backdropClick") return;
    setOpenDialog(false);
    reset(DEFAULT_FORM_VALUES);
    setShowPassword(false);
  };

  const onSubmitForm = async (formData) => {
    return new Promise((resolve, reject) => {
      createOrUpdateMutation.mutate(
        { formData, isCreateMode },
        {
          onSuccess: () => {
            handleCloseDialog();
            resolve();
          },
          onError: (error) => reject(error),
        }
      );
    });
  };

  const handleDeleteRequest = () => {
    if (!selectedUserId) return;
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedUserId) return;
    deleteMutation.mutate(selectedUserId);
    setDeleteDialogOpen(false);
  };

  const handleResetPassword = () => {
    if (!selectedUserId) return;
    resetPasswordMutation.mutate(selectedUserId);
  };

  const handleOpenInfo = (e) => setInfoAnchor(e.currentTarget);
  const handleCloseInfo = () => setInfoAnchor(null);
  const infoOpen = Boolean(infoAnchor);

  /* -------------------------
     Columns
  ------------------------- */
  const columns = useMemo(() => {
    const rolesLookup = roles || [];
    const permitsLookup = permits || [];

    return [
      {
        field: "email",
        headerName: "Email",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "name",
        headerName: "Full Name",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "nickname",
        headerName: "Nickname",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "roles",
        headerName: "Roles",
        minWidth: 200,
        flex: 1,
        valueGetter: (value) => {
          const roleIds = value || [];
          if (!roleIds.length) return null;
          return roleIds
            .map((roleId) => rolesLookup.find((r) => r.id === roleId)?.name)
            .filter(Boolean)
            .join(", ");
        },
        renderCell: ({ value }) => {
          if (!value) {
            return (
              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                No Roles Selected
              </Typography>
            );
          }
          return value;
        },
      },

      {
        field: "permits",
        headerName: "Permits",
        width: 160,
        valueGetter: (value) => {
          const permitIds = value || [];
          if (!permitIds.length) return null;
          return permitIds.map((permit) => {
            const p = permitsLookup?.find((p) => p.permit_ndx === permit);
            if (!p) return "N/A";
            return [`#${p.permit_ndx}`, p.contact_name, p.permittee]
              .filter(Boolean)
              .join(" | ");
          });
        },
        renderCell: ({ value }) => {
          if (!value) {
            return (
              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                No Permits Selected
              </Typography>
            );
          }

          return <ColumnsPopover name="Permits" items={value} />;
        },
      },
    ];
  }, [roles, permits]);

  /* -------------------------
     Custom Toolbar
  ------------------------- */
  const CustomToolbar = () => (
    <GridToolbarContainer
      sx={{ display: "flex", alignItems: "center", p: 2, gap: 1 }}
    >
      <Box sx={{ mr: "auto", display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleEditUser}
          disabled={!selectedUserId || isSubmitting || loading}
        >
          Edit
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteRequest}
          disabled={!selectedUserId || isSubmitting || loading}
        >
          Delete
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleAddUser}
          color="secondary"
          disabled={isSubmitting || loading}
        >
          Add User
        </Button>
        <GridToolbarExport
          slotProps={{
            button: {
              variant: "outlined",
              size: "medium",
            },
          }}
        />
      </Box>
    </GridToolbarContainer>
  );

  const handleRowSelection = (newSelection) => {
    setSelectedUserId(newSelection.length > 0 ? newSelection[0] : null);
  };

  const loading = loadingUsers || loadingPermits || loadingRoles;

  if (errorUsers) {
    return <div>Error fetching users: {String(errorUsers)}</div>;
  }

  return (
    <Box>
      <Helmet title="Users Management" />

      <Typography variant="h3" gutterBottom display="inline">
        Users Management
      </Typography>
      <Breadcrumbs sx={{ mt: 4 }} aria-label="Breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Users Management</Typography>
      </Breadcrumbs>
      <Divider sx={{ my: 6 }} />

      <Alert severity="info" variant="outlined" sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Manage system users:
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Create, edit, or delete accounts, and assign roles and permits.
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Quick actions:
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Click on a user in the table to enable <strong>Edit</strong> and{" "}
          <strong>Delete</strong> actions. Use the toolbar to{" "}
          <strong>Add New Users</strong> or <strong>Export User Data</strong>.
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          If you encounter any issues or have questions, please reach out to{" "}
          <strong>LRE Water</strong> for assistance.
        </Typography>
      </Alert>

      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Box>

      <Box sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredUsers}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.auth0_user_id}
          getRowHeight={() => "auto"}
          disableMultipleRowSelection
          onRowSelectionModelChange={handleRowSelection}
          rowSelectionModel={selectedUserId ? [selectedUserId] : []}
          slots={{ toolbar: CustomToolbar }}
          disableRowSelectionOnClick={false}
          sx={{
            [`& .${gridClasses.cell}`]: {
              py: 2,
            },
            "& .MuiDataGrid-cell:focus": { outline: "none" },
            "& .MuiDataGrid-cell:focus-within": { outline: "none" },
            "& .MuiDataGrid-row:hover": { cursor: "pointer" },
            "& .MuiDataGrid-row.Mui-selected": { backgroundColor: "#d9e7f5" },
            "& .MuiDataGrid-row.Mui-selected:hover": {
              backgroundColor: "#c3dbf2",
            },
          }}
        />
      </Box>

      <Dialog
        onClose={handleCloseDialog}
        open={openDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{isCreateMode ? "Add User" : "Edit User"}</DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box
              component="form"
              id="user-form"
              onSubmit={handleSubmit(onSubmitForm)}
            >
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    autoFocus
                    margin="normal"
                    label="Email"
                    fullWidth
                    autoComplete="email"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Full Name"
                    fullWidth
                    autoComplete="name"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="nickname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="normal"
                    label="Nickname"
                    fullWidth
                    autoComplete="nickname"
                  />
                )}
              />

              {isCreateMode && (
                <Controller
                  name="password"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      error={!!fieldState.error}
                    >
                      <InputLabel htmlFor="password">Password</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <IconButton
                              onClick={handleOpenInfo}
                              edge="start"
                              color={
                                fieldState.error?.message
                                  ? "error"
                                  : "secondary"
                              }
                            >
                              <InfoIcon />
                            </IconButton>
                          </InputAdornment>
                        }
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                        label="Password"
                      />
                      {fieldState.error?.message && (
                        <FormHelperText error>
                          {fieldState.error.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              )}

              <Popover
                open={infoOpen}
                anchorEl={infoAnchor}
                onClose={handleCloseInfo}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                  paper: {
                    sx: { p: 3, maxWidth: 280, border: "1px solid gray" },
                  },
                }}
              >
                <Box>
                  <strong>Password Requirements:</strong>
                  <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                    <li>At least 8 characters.</li>
                    <li>Must contain at least 3 of the following:</li>
                    <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                      <li>Uppercase letter (A-Z)</li>
                      <li>Lowercase letter (a-z)</li>
                      <li>Number (0-9)</li>
                      <li>Special character (!@#$%^&*)</li>
                    </ul>
                  </ul>
                </Box>
              </Popover>

              <Controller
                name="roles"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    multiple
                    {...field}
                    options={roles || []}
                    getOptionLabel={(option) => option.name || ""}
                    value={(roles || []).filter((r) =>
                      field.value.includes(r.id)
                    )}
                    onChange={(_, newValue) =>
                      field.onChange(newValue.map((v) => v.id))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="normal"
                        label="Roles"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="permits"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    {...field}
                    options={[TOGGLE_ALL_OPTION, ...sortedPermits]}
                    getOptionLabel={(option) => {
                      // Check if the option is the TOGGLE_ALL_OPTION
                      if (option?.id === TOGGLE_ALL_OPTION.id) {
                        return option.label;
                      }
                      // Otherwise, format the permit label as needed
                      return [
                        option?.permit_ndx ? `#${option.permit_ndx}` : null,
                        option?.contact_name,
                        option?.permittee,
                      ]
                        .filter(Boolean)
                        .join(" | ");
                    }}
                    value={sortedPermits.filter((p) =>
                      field.value.includes(p.permit_ndx)
                    )}
                    onChange={(_, newVal) => {
                      const isToggleAllClicked = newVal.some(
                        (v) => v.id === TOGGLE_ALL_OPTION.id
                      );
                      const allIds = sortedPermits.map((v) => v.permit_ndx);
                      field.onChange(
                        isToggleAllClicked
                          ? field.value.length === allIds.length
                            ? []
                            : allIds
                          : newVal.map((v) => v.permit_ndx)
                      );
                    }}
                    renderTags={(selected) =>
                      selected.length > 0
                        ? [`${selected.length} permit(s) selected`]
                        : []
                    }
                    renderInput={(params) => (
                      <TextField {...params} margin="normal" label="Permits" />
                    )}
                  />
                )}
              />

              {!isCreateMode && (
                <Box mt={3}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleResetPassword}
                    disabled={resetPasswordMutation.isLoading}
                    sx={{ width: 220 }}
                  >
                    {resetPasswordMutation.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Send Password Reset Email"
                    )}
                  </Button>
                  <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      This will send a <strong>password reset email</strong> to
                      the user's <strong>currently saved email address</strong>{" "}
                      in the system.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <em>Note:</em> If you need to send the reset email to a
                      different address, please update the user's email first
                      and save the changes before triggering a password reset.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isSubmitting || loading}
            onClick={handleCloseDialog}
          >
            Cancel
          </Button>
          <Button
            sx={{ width: 100 }}
            form="user-form"
            type="submit"
            variant="contained"
            color="secondary"
            disabled={!isDirty || isSubmitting || loading}
          >
            {isSubmitting ? (
              <CircularProgress color="#fff" size={24} />
            ) : isCreateMode ? (
              "Create"
            ) : (
              "Update"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this user?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
